import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check, Users, Star, Calculator, Phone, Shield, Award, TrendingUp, AlertCircle, CheckCircle, Zap, Wifi, Smartphone, Upload, ExternalLink, DollarSign, Crown, X } from 'lucide-react';
import './PrepaidHero.css';

// Simplified organic blob backgrounds
const OrganicBlobs = () => (
  <div className="organic-blobs">
    <div className="organic-blob blob-1"></div>
    <div className="organic-blob blob-2"></div>
  </div>
);

// Simplified floating particles
const EnhancedParticles = () => (
  <div className="enhanced-particles">
    {[...Array(12)].map((_, i) => (
      <div 
        key={i} 
        className={`enhanced-particle particle-${i % 3}`}
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          width: `${Math.random() * 3 + 2}px`,
          height: `${Math.random() * 3 + 2}px`,
          animationDelay: `${Math.random() * 6}s`,
          animationDuration: `${Math.random() * 8 + 8}s`
        }}
      />
    ))}
  </div>
);

// Improved headline slider with better consumer messaging
const BenefitHeadlineSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const headlines = [
    "You're paying less, But Missing More",
    "Premium Plans. Group Discounts. No Compromises.",
    "Upgrade Your Phone Plan - Not Your Bill",
    "Premium Features at Prepaid-Smart Prices"
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % headlines.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="headline-slider">
      <AnimatePresence mode="wait">
        {headlines.map((headline, index) => (
          index === currentIndex && (
            <motion.h1 
              key={headline}
              className="hero-title"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.6 }}
            >
              {headline}
            </motion.h1>
          )
        ))}
      </AnimatePresence>
    </div>
  );
};

// Enhanced trust badges - Better consumer messaging
const EnhancedTrustBadges = () => (
  <motion.div 
    className="trust-badges"
    initial={{ opacity: 0, y: 15 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 0.4 }}
  >
    <motion.div className="trust-badge premium" whileHover={{ scale: 1.05 }}>
      <DollarSign size={12} />
      <span>Still Budget-Friendly</span>
    </motion.div>
    <motion.div className="trust-badge carrier" whileHover={{ scale: 1.05 }}>
      <Shield size={12} />
      <span>Keep Your Carrier</span>
    </motion.div>
    <motion.div className="trust-badge value" whileHover={{ scale: 1.05 }}>
      <Crown size={12} />
      <span>Get More for Less</span>
    </motion.div>
  </motion.div>
);

// Enhanced feature items with better value props
const ValueFeatureItem = ({ mainText, supportingText, delay = 0, icon: Icon = Check }) => {
  return (
    <motion.div 
      className="enhanced-feature"
      initial={{ opacity: 0, x: -15 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.8 + delay * 0.1 }}
      whileHover={{ scale: 1.02 }}
    >
      <motion.div className="enhanced-feature-icon-container">
        <Icon size={14} className="enhanced-feature-icon" />
      </motion.div>
      <div className="enhanced-feature-text-container">
        <span className="enhanced-feature-text">{mainText}</span>
        <span className="enhanced-feature-subtext">{supportingText}</span>
      </div>
    </motion.div>
  );
};

// Lead Capture Modal Component
const LeadCaptureModal = ({ isOpen, onClose, calculatorData }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    hasUploadedBill: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setIsSuccess(true);
    
    // Track conversion
    if (window.gtag) {
      window.gtag('event', 'premium_upgrade_lead', {
        event_category: 'Lead Generation',
        event_label: 'Premium Upgrade Request',
        value: calculatorData?.upgrade?.annualIncrease || 0
      });
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, hasUploadedBill: true }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="lead-capture-modal-overlay" onClick={onClose}>
      <motion.div 
        className="lead-capture-modal"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        onClick={e => e.stopPropagation()}
      >
        {!isSuccess ? (
          <>
            <div className="lead-capture-header">
              <h3>🔓 Unlock Your Premium Upgrade</h3>
              <button onClick={onClose} className="lead-capture-close">
                <X size={20} />
              </button>
            </div>
            
            <div className="lead-capture-content">
              {calculatorData && (
                <div className="lead-capture-summary">
                  <div className="summary-highlight">
                    <span className="summary-carrier">{calculatorData.upgrade.carrierName}</span>
                    <span className="summary-lines">{calculatorData.numberOfLines} lines</span>
                    <span className="summary-savings">${calculatorData.upgrade.annualIncrease.toLocaleString()}/year savings</span>
                  </div>
                </div>
              )}
              
              <p className="lead-capture-description">
                Get your personalized premium group plan quote within 24 hours. We'll show you exactly how to upgrade your features — no spam, no pressure.
              </p>
              
              <form onSubmit={handleSubmit} className="lead-capture-form">
                <div className="form-row">
                  <input
                    type="text"
                    placeholder="First Name (optional)"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="lead-capture-input"
                  />
                </div>
                
                <div className="form-row">
                  <input
                    type="email"
                    placeholder="Email Address *"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="lead-capture-input"
                    required
                  />
                </div>
                
                <div className="form-row">
                  <input
                    type="tel"
                    placeholder="Phone Number (for faster quotes)"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="lead-capture-input"
                  />
                </div>
                
                <div className="bill-upload-section-modal">
                  <label className="bill-upload-label">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleFileUpload}
                      className="bill-upload-input-hidden"
                    />
                    <div className="bill-upload-button-modal">
                      <Upload size={16} />
                      <span>{formData.hasUploadedBill ? 'Bill Uploaded ✓' : 'Upload Your Bill (optional)'}</span>
                    </div>
                  </label>
                  <p className="bill-upload-help">For the most accurate quote, upload your latest phone bill</p>
                </div>
                
                <button 
                  type="submit" 
                  className="lead-capture-submit"
                  disabled={isSubmitting || !formData.email}
                >
                  {isSubmitting ? (
                    <>
                      <div className="loading-spinner"></div>
                      Getting Your Quote...
                    </>
                  ) : (
                    <>
                      <Crown size={16} />
                      Get My Premium Upgrade Quote
                    </>
                  )}
                </button>
                
                <p className="lead-capture-disclaimer">
                  No spam, no pressure. We'll email you a detailed breakdown and a real person will follow up within 1 business day.
                </p>
              </form>
            </div>
          </>
        ) : (
          <div className="lead-capture-success">
            <div className="success-icon">
              <CheckCircle size={48} />
            </div>
            <h3>Quote Request Received! 🎉</h3>
            <p>We're analyzing your {calculatorData?.upgrade.carrierName} plan and will email your personalized premium upgrade quote to <strong>{formData.email}</strong> within 24 hours.</p>
            
            <div className="success-next-steps">
              <h4>What happens next:</h4>
              <ul>
                <li>📧 Detailed upgrade analysis in your inbox</li>
                <li>📞 Personal call from our team within 1 business day</li>
                <li>🚀 Most customers start saving within 2 weeks</li>
              </ul>
            </div>
            
            <button onClick={onClose} className="success-close-btn">
              Perfect, Thanks!
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

// Improved and more compact Plan Upgrade Calculator
const SmartUpgradeCalculator = () => {
  const [selectedCarrier, setSelectedCarrier] = useState('mint');
  const [selectedPlan, setSelectedPlan] = useState('5gb');
  const [numberOfLines, setNumberOfLines] = useState(2);
  const [isMobile, setIsMobile] = useState(false);
  const [showMoreCarriers, setShowMoreCarriers] = useState(false);
  const [showBillUpload, setShowBillUpload] = useState(false);
  const [showDetailedComparison, setShowDetailedComparison] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Enhanced carrier data with specific plans - Top prepaid carriers
  const carrierData = {
    // Top 3 carriers (shown as pills)
    mint: {
      name: 'Mint Mobile',
      popular: true,
      plans: {
        '5gb': { name: '5GB Plan', cost: 20 },
        '15gb': { name: '15GB Plan', cost: 25 },
        'unlimited': { name: 'Unlimited Plan', cost: 30 }
      }
    },
    visible: {
      name: 'Visible',
      popular: true,
      plans: {
        'basic': { name: 'Visible Basic', cost: 30 },
        'plus': { name: 'Visible+', cost: 45 }
      }
    },
    usmobile: {
      name: 'US Mobile',
      popular: true,
      plans: {
        'unlimited': { name: 'Unlimited Starter', cost: 25 },
        'premium': { name: 'Unlimited Premium', cost: 35 }
      }
    },
    // Additional carriers (shown in expanded view)
    cricket: {
      name: 'Cricket Wireless',
      popular: false,
      plans: {
        '5gb': { name: '5GB Plan', cost: 30 },
        '10gb': { name: '10GB Plan', cost: 40 },
        'unlimited': { name: 'Unlimited Plan', cost: 55 }
      }
    },
    metro: {
      name: 'Metro by T-Mobile',
      popular: false,
      plans: {
        '10gb': { name: '10GB Plan', cost: 40 },
        'unlimited': { name: 'Unlimited Plan', cost: 50 }
      }
    },
    boost: {
      name: 'Boost Mobile',
      popular: false,
      plans: {
        '5gb': { name: '5GB Plan', cost: 25 },
        'unlimited': { name: 'Unlimited Plan', cost: 50 }
      }
    },
    straighttalk: {
      name: 'Straight Talk',
      popular: false,
      plans: {
        '8gb': { name: '8GB Plan', cost: 35 },
        'unlimited': { name: 'Unlimited Plan', cost: 55 }
      }
    },
    totalwireless: {
      name: 'Total Wireless',
      popular: false,
      plans: {
        '5gb': { name: '5GB Plan', cost: 25 },
        'unlimited': { name: 'Unlimited Plan', cost: 50 }
      }
    },
    redpocket: {
      name: 'Red Pocket Mobile',
      popular: false,
      plans: {
        '8gb': { name: '8GB Plan', cost: 30 },
        'unlimited': { name: 'Unlimited Plan', cost: 60 }
      }
    }
  };
  
  const getPopularCarriers = () => {
    return Object.entries(carrierData).filter(([key, carrier]) => carrier.popular);
  };
  
  const getAdditionalCarriers = () => {
    return Object.entries(carrierData).filter(([key, carrier]) => !carrier.popular);
  };
  
  const handleCarrierSelect = (carrierKey) => {
    setSelectedCarrier(carrierKey);
    setSelectedPlan(Object.keys(carrierData[carrierKey].plans)[0]);
    setShowMoreCarriers(false);
    setShowBillUpload(false);
  };
  
  const calculateUpgrade = () => {
    const carrier = carrierData[selectedCarrier];
    const plan = carrier.plans[selectedPlan];
    const smartUpgradeCost = 35; // Group-optimized postpaid equivalent
    
    const monthlyIncrease = (smartUpgradeCost - plan.cost) * numberOfLines;
    const annualIncrease = monthlyIncrease * 12;
    const percentageIncrease = Math.round(((smartUpgradeCost - plan.cost) / plan.cost) * 100);
    
    const bonusValue = 500; // Estimated value of carrier perks, credits, etc.
    
    return {
      currentCost: plan.cost,
      smartUpgradeCost,
      monthlyIncrease,
      annualIncrease,
      percentageIncrease,
      carrierName: carrier.name,
      planName: plan.name,
      bonusValue,
      numberOfLines
    };
  };
  
  const upgrade = calculateUpgrade();
  
  const formatCurrency = (amount) => {
    if (isMobile && Math.abs(amount) >= 1000) {
      return `${(amount / 1000).toFixed(1)}K`;
    }
    return `${Math.abs(amount).toLocaleString()}`;
  };
  
  const getPlanOptions = () => {
    return Object.entries(carrierData[selectedCarrier].plans).map(([key, plan]) => ({
      key,
      label: plan.name
    }));
  };
  
  return (
    <motion.div 
      className="smart-upgrade-calculator"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
      whileHover={!isMobile ? { scale: 1.01 } : {}}
    >
      {/* Compact Header */}
      <div className="calculator-header">
        <div className="calculator-badge">
          <Zap size={10} />
          <span>See what you're missing</span>
        </div>
        
        <h2 className="calculator-title">Plan Upgrade Calculator</h2>
        <p className="calculator-subtitle">Choose your current plan, and we'll show the upgrade</p>
      </div>
      
      {/* Compact Interactive Inputs */}
      <div className="calculator-inputs">
        <div className="input-group">
          <label className="input-label">Current carrier:</label>
          <div className="carrier-buttons">
            {getPopularCarriers().map(([key, carrier]) => (
              <button
                key={key}
                onClick={() => handleCarrierSelect(key)}
                className={`carrier-button ${selectedCarrier === key ? 'selected' : ''}`}
              >
                {carrier.name}
              </button>
            ))}
            <button
              onClick={() => setShowMoreCarriers(!showMoreCarriers)}
              className={`carrier-button more-carriers-button ${showMoreCarriers ? 'selected' : ''}`}
            >
              {showMoreCarriers ? 'Show Less' : 'See More Carriers'}
            </button>
          </div>
          
          {/* Expanded Carriers List */}
          <AnimatePresence>
            {showMoreCarriers && (
              <motion.div
                className="expanded-carriers"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="expanded-carriers-grid">
                  {getAdditionalCarriers().map(([key, carrier]) => (
                    <button
                      key={key}
                      onClick={() => handleCarrierSelect(key)}
                      className={`expanded-carrier-card ${selectedCarrier === key ? 'selected' : ''}`}
                    >
                      <span className="carrier-name">{carrier.name}</span>
                      <span className="carrier-plans">{Object.keys(carrier.plans).length} plans</span>
                    </button>
                  ))}
                  
                  {/* Bill Upload Fallback */}
                  <button
                    onClick={() => setShowBillUpload(true)}
                    className="expanded-carrier-card upload-fallback"
                  >
                    <Upload size={16} />
                    <span className="carrier-name">Don't see yours?</span>
                    <span className="carrier-plans">Upload your bill</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Bill Upload Modal/Section */}
          <AnimatePresence>
            {showBillUpload && (
              <motion.div
                className="bill-upload-modal"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bill-upload-modal-content">
                  <div className="bill-upload-modal-header">
                    <h4>Upload Your Bill</h4>
                    <button 
                      onClick={() => setShowBillUpload(false)}
                      className="bill-upload-close"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <p>Don't see your carrier? No problem! Upload your latest phone bill and we'll review it to show you exactly how to upgrade your plan — not your price.</p>
                  <button className="bill-upload-modal-button">
                    <Upload size={14} />
                    Choose File to Upload
                  </button>
                  <div className="bill-upload-modal-footer">
                    <span>We'll email you a personalized upgrade analysis within 24 hours</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        <div className="input-group">
          <label className="input-label">Your current plan:</label>
          <div className="plan-buttons">
            {getPlanOptions().map((plan) => (
              <button
                key={plan.key}
                onClick={() => setSelectedPlan(plan.key)}
                className={`plan-button ${selectedPlan === plan.key ? 'selected' : ''}`}
              >
                {plan.label}
              </button>
            ))}
          </div>
        </div>
        
        <div className="input-group">
          <label className="input-label">
            Number of lines: <span className="line-count-value">{numberOfLines}</span> {numberOfLines === 1 ? 'line' : 'lines'}
          </label>
          <input
            type="range"
            min="1"
            max="10"
            value={numberOfLines}
            onChange={(e) => setNumberOfLines(Number(e.target.value))}
            className="line-count-slider"
          />
          <div className="slider-labels">
            <span>1</span>
            <span>10+</span>
          </div>
        </div>
      </div>
      
      {/* Compact Plan Comparison */}
      <div className="plan-comparison-section">
        <h3 className="comparison-title">
          <Wifi size={14} />
          Your Plan vs. Smart Upgrade
        </h3>
        
        <div className="comparison-table">
          <div className="comparison-header">
            <div className="comparison-col current">Your Current Plan</div>
            <div className="comparison-col upgraded">Your Smart Upgrade</div>
          </div>
          
          <div className="comparison-row">
            <div className="comparison-cell current">
              <div className="plan-name">{upgrade.planName}</div>
              <div className="plan-price">${upgrade.currentCost}/mo per line</div>
            </div>
            <div className="comparison-cell upgraded">
              <div className="plan-name">Premium Group Plan</div>
              <div className="plan-price">${upgrade.smartUpgradeCost}/mo per line</div>
            </div>
          </div>
          
          {/* Compact Feature Grid */}
          <div className="feature-comparison-grid">
            <div className="feature-row">
              <span className="feature-label">Hotspot</span>
              <div className="feature-current">
                <AlertCircle size={10} />
                <span>None or 600kbps cap</span>
              </div>
              <div className="feature-upgraded">
                <CheckCircle size={10} />
                <span>Full-speed hotspot</span>
              </div>
            </div>
            
            <div className="feature-row">
              <span className="feature-label">Streaming</span>
              <div className="feature-current">
                <AlertCircle size={10} />
                <span>480p max</span>
              </div>
              <div className="feature-upgraded">
                <CheckCircle size={10} />
                <span>4K UHD streaming</span>
              </div>
            </div>
            
            <div className="feature-row">
              <span className="feature-label">Data Priority</span>
              <div className="feature-current">
                <AlertCircle size={10} />
                <span>Deprioritized</span>
              </div>
              <div className="feature-upgraded">
                <CheckCircle size={10} />
                <span>Premium access</span>
              </div>
            </div>
            
            <div className="feature-row">
              <span className="feature-label">Perks & Credits</span>
              <div className="feature-current">
                <AlertCircle size={10} />
                <span>None</span>
              </div>
              <div className="feature-upgraded">
                <CheckCircle size={10} />
                <span>$550+ credits</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Compact Investment Summary */}
      <motion.div className="investment-summary">
        <h3 className="investment-title">
          <Calculator size={14} />
          Your Upgrade Advantage
        </h3>
        
        <div className="investment-grid">
          <div className="investment-item">
            <div className="investment-label">Monthly Investment</div>
            <div className="investment-value">+${formatCurrency(upgrade.monthlyIncrease)}</div>
            <div className="investment-subtitle">For premium-level features</div>
          </div>
          
          <div className="investment-item">
            <div className="investment-label">Total Value Added</div>
            <div className="investment-value highlight">${formatCurrency(upgrade.bonusValue + upgrade.annualIncrease)}</div>
            <div className="investment-subtitle">Including $550+ in promo credits</div>
          </div>
        </div>

        <div className="investment-disclaimer">
          <strong>Smart upgrade:</strong> Get postpaid-grade features while keeping your price-conscious mindset. Based on current {upgrade.carrierName} pricing.
        </div>

        {/* Enhanced CTA - Show Me What I'm Missing */}
        <motion.button 
          className="calculator-cta-button"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.4 }}
          whileHover={{ scale: isMobile ? 1 : 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowDetailedComparison(!showDetailedComparison)}
        >
          <Star size={12} />
          <span>{showDetailedComparison ? 'Hide Details' : 'Show Me What I\'m Missing'}</span>
          <ArrowRight size={10} />
        </motion.button>
        
        {/* Detailed Comparison Expansion */}
        <AnimatePresence>
          {showDetailedComparison && (
            <motion.div
              className="detailed-comparison-section"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4 }}
            >
              <h4 className="detailed-comparison-title">
                <Zap size={14} />
                Here's Everything You're Missing Out On
              </h4>
              
              <div className="detailed-features-grid">
                <div className="feature-detail-card">
                  <div className="feature-detail-icon">📶</div>
                  <div className="feature-detail-content">
                    <h5>Premium Network Priority</h5>
                    <p>Your data gets priority during busy times - no more slowdowns at concerts, airports, or crowded areas.</p>
                    <span className="feature-detail-value">vs. Deprioritized on {upgrade.carrierName}</span>
                  </div>
                </div>
                
                <div className="feature-detail-card">
                  <div className="feature-detail-icon">📱</div>
                  <div className="feature-detail-content">
                    <h5>Full-Speed Unlimited Hotspot</h5>
                    <p>Turn your phone into a real Wi-Fi router - work from anywhere, stream on your laptop, no caps.</p>
                    <span className="feature-detail-value">vs. None or 600kbps cap</span>
                  </div>
                </div>
                
                <div className="feature-detail-card">
                  <div className="feature-detail-icon">🎬</div>
                  <div className="feature-detail-content">
                    <h5>4K UHD Streaming</h5>
                    <p>Netflix, YouTube, Disney+ in true 4K quality - your phone and TV deserve better than 480p.</p>
                    <span className="feature-detail-value">vs. 480p max on most prepaid</span>
                  </div>
                </div>
                
                <div className="feature-detail-card">
                  <div className="feature-detail-icon">💰</div>
                  <div className="feature-detail-content">
                    <h5>$550+ in Carrier Perks</h5>
                    <p>Apple Music, Netflix credits, T-Mobile Tuesdays, device discounts - perks that actually pay you back.</p>
                    <span className="feature-detail-value">vs. Zero perks on prepaid</span>
                  </div>
                </div>
                
                <div className="feature-detail-card">
                  <div className="feature-detail-icon">🌍</div>
                  <div className="feature-detail-content">
                    <h5>International Features</h5>
                    <p>Free texting to 200+ countries, discounted international calling, roaming options.</p>
                    <span className="feature-detail-value">vs. Expensive add-ons</span>
                  </div>
                </div>
                
                <div className="feature-detail-card">
                  <div className="feature-detail-icon">🏆</div>
                  <div className="feature-detail-content">
                    <h5>Premium Customer Support</h5>
                    <p>Skip the hold times - priority support lines, live chat, dedicated account management.</p>
                    <span className="feature-detail-value">vs. Basic support only</span>
                  </div>
                </div>
              </div>
              
              <div className="detailed-comparison-cta">
                <p className="detailed-comparison-summary">
                  <strong>Bottom line:</strong> You're missing $550+ in value every year, plus premium speeds and features that make your phone actually work like a premium device.
                </p>
                
                <button 
                  className="detailed-comparison-upgrade-btn"
                  onClick={() => {
                    // This will trigger the lead capture modal
                    const event = new CustomEvent('openLeadCapture', { 
                      detail: { 
                        source: 'detailed_comparison',
                        calculatorData: { selectedCarrier, selectedPlan, numberOfLines, upgrade }
                      }
                    });
                    window.dispatchEvent(event);
                  }}
                >
                  <Crown size={14} />
                  I Want These Premium Features
                  <ArrowRight size={12} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

// Simplified CTA button
const SmartCTAButton = ({ children, icon, ...props }) => {
  return (
    <motion.button 
      className="smart-cta primary"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 1.1 }}
      whileHover={{ 
        scale: 1.05, 
        boxShadow: "0 16px 32px rgba(139, 92, 246, 0.45), 0 0 0 2px rgba(255, 255, 255, 0.15) inset"
      }}
      whileTap={{ scale: 0.98 }}
      {...props}
    >
      <span className="button-text">{children}</span>
      <div className="button-icon-container">
        {icon || <ArrowRight size={16} className="button-icon" />}
      </div>
    </motion.button>
  );
};

// Bill upload section
const BillUploadSection = () => (
  <motion.div 
    className="bill-upload-section"
    initial={{ opacity: 0, y: 15 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 1.3 }}
  >
    <div className="bill-upload-content">
      <div className="bill-upload-icon">
        <Upload size={20} />
      </div>
      <div className="bill-upload-text">
        <h4>Not Sure What You're Missing?</h4>
        <p>Upload your latest phone bill. We'll review it and show you how to upgrade your plan — not your price.</p>
      </div>
    </div>
    <button className="bill-upload-button">
      <Upload size={14} />
      📤 Upload My Bill & Get Free Review
    </button>
  </motion.div>
);

// Enhanced testimonials section
const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "I kept my number, stayed with Mint — but got full-speed hotspot and faster everything.",
      author: "Asha P.",
      detail: "Former Mint Mobile User",
      avatar: "A"
    },
    {
      quote: "This felt like unlocking Verizon perks without the Verizon price.",
      author: "Eli R.",
      detail: "Former Visible User",
      avatar: "E"
    },
    {
      quote: "Same carrier, same number — just way better features for barely more money.",
      author: "Jordan K.",
      detail: "Former US Mobile User",
      avatar: "J"
    }
  ];

  return (
    <motion.div 
      className="testimonials-section"
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 1.4 }}
    >
      <h3 className="testimonials-title">What Smart Upgraders Say</h3>
      <div className="testimonials-grid">
        {testimonials.map((testimonial, index) => (
          <motion.div 
            key={index}
            className="testimonial-card"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 1.5 + index * 0.1 }}
            whileHover={{ scale: 1.02 }}
          >
            <div className="testimonial-quote">"{testimonial.quote}"</div>
            <div className="testimonial-author">
              <div className="author-avatar">{testimonial.avatar}</div>
              <div className="author-info">
                <span className="author-name">{testimonial.author}</span>
                <span className="author-detail">{testimonial.detail}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

const PrepaidHero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [leadCaptureOpen, setLeadCaptureOpen] = useState(false);
  const [calculatorData, setCalculatorData] = useState(null);
  const heroRef = useRef(null);
  
  useEffect(() => {
    setIsVisible(true);
    
    // Listen for lead capture modal triggers
    const handleOpenLeadCapture = (event) => {
      setCalculatorData(event.detail.calculatorData);
      setLeadCaptureOpen(true);
    };
    
    window.addEventListener('openLeadCapture', handleOpenLeadCapture);
    
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      
      const heroElement = heroRef.current;
      if (heroElement && window.innerWidth > 768) {
        // Subtle parallax effects
        const blobs = heroElement.querySelectorAll('.organic-blob');
        blobs.forEach((blob, index) => {
          const speed = 0.015 + (index * 0.01);
          blob.style.transform = `translateY(${scrollPosition * speed}px)`;
        });
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('openLeadCapture', handleOpenLeadCapture);
    };
  }, []);
  
  const handlePrimaryClick = () => {
    setLeadCaptureOpen(true);
  };
  
  const handleLeadCaptureClose = () => {
    setLeadCaptureOpen(false);
  };

  return (
    <div className="prepaid-hero-wrapper">
      <section className="hero-section" ref={heroRef}>
        {/* Background elements */}
        <div className="noise-overlay"></div>
        <OrganicBlobs />
        <EnhancedParticles />
        
        {/* Gradient overlays */}
        <div className="gradient-overlay gradient-1"></div>
        <div className="gradient-overlay gradient-2"></div>
        
        <div className="hero-container">
          <div className="hero-content">
            <BenefitHeadlineSlider />
            
            <motion.p 
              className="hero-subtitle"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 15 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              You chose prepaid to save money — smart move. But those savings come with trade-offs: capped data, slow hotspot, and missing perks. <br className="hero-subtitle-break" />
              <strong>CircledIn</strong> helps smart budgeters upgrade to <strong>Verizon, AT&T, or T-Mobile</strong> postpaid plans at group-negotiated prices — no fluff, no reseller markups, just real premium value.
            </motion.p>
            
            <EnhancedTrustBadges />
            
            <motion.div className="value-features">
              <ValueFeatureItem 
                mainText="Ditch Prepaid Limits" 
                supportingText="Upgrade from capped data, slow hotspot, and 480p streaming to full-speed, postpaid-level service." 
                delay={0}
                icon={Shield}
              />
              <ValueFeatureItem 
                mainText="Group Discounts on Big 3 Plans" 
                supportingText="We group users to unlock elite pricing on Verizon, AT&T, or T-Mobile — the same networks, better rates." 
                delay={1}
                icon={Crown}
              />
              <ValueFeatureItem 
                mainText="Smart Budget, Not Budget Service" 
                supportingText="Still saving — just without the trade-offs. Postpaid perks, prepaid discipline." 
                delay={2}
                icon={DollarSign}
              />
              <ValueFeatureItem 
                mainText="Keep Your Number, Not the Compromise" 
                supportingText="Switch without the hassle — your number stays, your experience levels up." 
                delay={3}
                icon={DollarSign}
              />
            </motion.div>
            
            <div className="cta-container">
              <SmartCTAButton 
                icon={<Star size={16} />}
                onClick={handlePrimaryClick}
              >
                🔓 Unlock My Premium Upgrade
              </SmartCTAButton>
            </div>
            
            {/* Microcopy for anxiety reduction */}
            <motion.div 
              className="cta-microcopy"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 1.2 }}
            >
              <span>No switching. No spam. No pressure.</span>
            </motion.div>
          </div>
          
          <div className="hero-visual">
            <SmartUpgradeCalculator />
            
            {/* Social Proof */}
            <motion.div 
              className="social-proof-section"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.6 }}
            >
              <div className="social-proof-content">
                <Star size={14} className="social-proof-icon" />
                <span className="social-proof-text">
                  Trusted by <strong>500+ budget-conscious users</strong>
                </span>
              </div>
              <div className="social-proof-amount">
                <span className="amount-highlight">$2.8M+</span> in premium value unlocked
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Additional sections */}
      <section className="additional-content">
        <div className="content-container">
          <BillUploadSection />
          <TestimonialsSection />
          
          {/* What happens next */}
          <motion.div 
            className="what-happens-next"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.8 }}
          >
            <h3>What Happens Next?</h3>
            <div className="steps-grid">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h4>We Review Your Plan</h4>
                  <p>We analyze your current setup for savings and premium gaps</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h4>You See Your Options</h4>
                  <p>We show you how to upgrade your plan — no switching needed</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h4>You Upgrade When Ready</h4>
                  <p>Same number, same carrier, just way better everything</p>
                </div>
              </div>
            </div>
            <p className="steps-subtitle">⏱️ Takes 60 seconds. No spam. No pressure.</p>
          </motion.div>

          {/* Final CTA */}
          <motion.div 
            className="final-cta-section"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 2.0 }}
          >
            <h3>🎯 Ready to Stop Sacrificing Speed for Savings?</h3>
            <div className="final-cta-buttons">
              <SmartCTAButton 
                icon={<Star size={16} />}
                onClick={handlePrimaryClick}
              >
                🔓 Unlock My Premium Upgrade
              </SmartCTAButton>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Lead Capture Modal */}
      <AnimatePresence>
        {leadCaptureOpen && (
          <LeadCaptureModal
            isOpen={leadCaptureOpen}
            onClose={handleLeadCaptureClose}
            calculatorData={calculatorData}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PrepaidHero;